import { useSession } from 'next-auth/react'
import { useEffect, useRef } from 'react'
import { clearCookie, getCookie, setCookie } from '@omnea-app/fe-browser-utils/cookies'
import { isProductionEnvironment, isStagingEnvironment } from '@omnea-app/fe-legacy/components/layout/wrapper/appUtils'
import {
  PRODUCTION_RECORDING_COOKIE,
  PRODUCTION_RECORDING_COOKIE_MAX_AGE_SECONDS,
  PRODUCTION_RECORDING_MIDDLEWARE,
  isProductionRecordingAllowed,
} from './productionRecording'
import type { MeticulousPublicApi } from '@alwaysmeticulous/sdk-bundles-api'
// oxlint-disable-next-line import/no-empty-named-blocks -- Load NextAuth type augmentation without a runtime import.
import type {} from '@omnea-app/fe-auth/nextAuthOverrides'
import type { SessionUser } from '@omnea-app/fe-auth/session'

const recordUserContext = (meticulous: MeticulousPublicApi['context'], user: SessionUser | undefined) => {
  try {
    if (user?.email) {
      meticulous.recordUserEmail(user.email)
    }

    if (user?.type === 'fullyAuthenticated' && user.metadata?.id) {
      meticulous.recordUserId(user.metadata.id)
      meticulous.recordCustomContext('organisationId', user.orgMetadata.organisationId)
      meticulous.recordCustomContext('organisationName', user.orgMetadata.name)
      meticulous.recordCustomContext('userRole', user.metadata.role)
    }

    meticulous.recordCustomContext('browserLanguage', navigator.language)

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      meticulous.recordCustomContext('timezone', timezone)
    } catch {
      meticulous.recordCustomContext('timezone', 'unknown')
    }
  } catch (error) {
    console.error('[Meticulous] Failed to record user context:', error)
  }

  // Note: Feature flags are automatically recorded via LaunchDarkly and org-settings inspectors
}

const decideProductionRecording = async (user: SessionUser | undefined) => {
  const recordingToken = process.env.NEXT_PUBLIC_METICULOUS_RECORDING_TOKEN
  const loadedEarlyNetworkRecorder = getCookie(PRODUCTION_RECORDING_COOKIE) === '1'
  const isAllowed = user?.type === 'fullyAuthenticated' && isProductionRecordingAllowed(user)

  if (isAllowed) {
    setCookie(PRODUCTION_RECORDING_COOKIE, '1', PRODUCTION_RECORDING_COOKIE_MAX_AGE_SECONDS)
  } else if (user?.type === 'fullyAuthenticated') {
    clearCookie(PRODUCTION_RECORDING_COOKIE)
  }

  if (!loadedEarlyNetworkRecorder) {
    return
  }

  const { stopIntercepting, tryLoadAndStartRecorder } = await import('@alwaysmeticulous/recorder-loader')
  if (!isAllowed || !recordingToken) {
    await stopIntercepting()
    return
  }

  await tryLoadAndStartRecorder({
    recordingToken,
    isProduction: true,
    // Production mode abandons a session past 1M characters of network data, and this app needs more.
    maxPayloadSize: 20_000_000,
    middleware: PRODUCTION_RECORDING_MIDDLEWARE,
  })
  if (window.Meticulous?.context) {
    recordUserContext(window.Meticulous.context, user)
  }
}

export const SessionMeticulous = () => {
  const { data: session, status } = useSession()
  const hasDecidedProductionRecording = useRef(false)

  useEffect(() => {
    if (isStagingEnvironment()) {
      return
    }

    if (!isProductionEnvironment()) {
      if (window.Meticulous?.context) {
        recordUserContext(window.Meticulous.context, session?.user)
      }
      return
    }

    if (status === 'loading' || hasDecidedProductionRecording.current) {
      return
    }
    hasDecidedProductionRecording.current = true
    void decideProductionRecording(session?.user)
  }, [session, status])

  return null
}
